import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  Mail, Send, AlertTriangle, Eye, EyeOff, Loader2, Bell, Gift, Share2, Printer,
  RefreshCw, Building, CreditCard, Megaphone, Users, CheckCircle, XCircle, Save,
} from 'lucide-react';

/**
 * Tab "email" – ausgelagerte Tab-Komponente.
 * Erhält alle benötigten State- und Handler-Referenzen vom Parent via Props.
 */
const SettingsEmail = ({ ctx }) => {
  const { t } = useTranslation();
  const {
    settings, setSettings, handleInputChange,
    showSmtpPassword, setShowSmtpPassword,
    testEmailTo, setTestEmailTo, sendingTest, testResult, handleTestEmail,
    triggeringFollowUp, followUpResult, handleTriggerFollowUp,
    triggeringBackup, backupResult, handleTriggerBackup,
    healthStatus, loadHealthStatus, runHealthCheck, runningHealthCheck,
  } = ctx;

  return (
    <>

          {/* Gmail SMTP Settings */}
          <Card className="card-shadow mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Gmail E-Mail Konfiguration
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie Gmail SMTP, um E-Mails (Buchungsbestätigungen, Passwort-Reset) zu versenden.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-[hsl(var(--chart-3)/0.1)] border-[hsl(var(--chart-3))]">
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--chart-3))]" />
                <AlertDescription className="text-sm">
                  Verwenden Sie ein <strong>App-Passwort</strong> (nicht Ihr normales Google-Passwort).
                  Erstellen Sie eines unter{' '}
                  <a
                    href="https://myaccount.google.com/apppasswords"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[hsl(var(--primary))] hover:underline font-medium"
                  >
                    Google App-Passwörter
                  </a>.
                  Das Passwort wird in der Datenbank gespeichert.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label htmlFor="smtp_email">Gmail-Adresse</Label>
                <Input
                  id="smtp_email"
                  name="smtp_email"
                  type="email"
                  value={settings.smtp_email}
                  onChange={handleInputChange}
                  placeholder="ihre-firma@gmail.com"
                  data-testid="settings-smtp-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_password">App-Passwort</Label>
                <div className="relative">
                  <Input
                    id="smtp_password"
                    name="smtp_password"
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={settings.smtp_password}
                    onChange={handleInputChange}
                    placeholder="xxxx xxxx xxxx xxxx"
                    className="pr-10 font-mono text-sm"
                    data-testid="settings-smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    data-testid="settings-toggle-smtp-password"
                  >
                    {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Test Email Section */}
              <div className="pt-4 border-t border-[hsl(var(--border))]">
                <Label className="text-sm font-medium mb-2 block">Test-E-Mail senden</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                    placeholder="test@example.com"
                    className="flex-1"
                    data-testid="settings-test-email-input"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleTestEmail}
                    disabled={sendingTest || !testEmailTo}
                    data-testid="settings-test-email-button"
                  >
                    {sendingTest ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    <span className="ml-2">Testen</span>
                  </Button>
                </div>
                {testResult && (
                  <Alert className={`mt-3 ${
                    testResult.type === 'success'
                      ? 'bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]'
                      : 'bg-[hsl(var(--destructive)/0.1)] border-[hsl(var(--destructive))]'
                  }`}>
                    <AlertDescription className={`text-sm ${
                      testResult.type === 'success'
                        ? 'text-[hsl(var(--chart-2))]'
                        : 'text-[hsl(var(--destructive))]'
                    }`}>
                      {testResult.message}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Automatisches Backup */}
          <Card className="card-shadow mb-6" data-testid="backup-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-[hsl(var(--primary))]" />
                Automatisches Backup
              </CardTitle>
              <CardDescription>
                Datenbank-Backup automatisch per E-Mail an den Admin senden. SMTP muss konfiguriert sein.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="backup_enabled"
                  checked={settings.backup_enabled}
                  onChange={(e) => setSettings({...settings, backup_enabled: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="backup-enabled"
                />
                <Label htmlFor="backup_enabled" className="cursor-pointer">Automatisches Backup aktivieren</Label>
              </div>
              {settings.backup_enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-7">
                  <div>
                    <Label>Häufigkeit</Label>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-[hsl(var(--border))] bg-white text-sm"
                      value={settings.backup_frequency}
                      onChange={(e) => setSettings({...settings, backup_frequency: e.target.value})}
                      data-testid="backup-frequency"
                    >
                      <option value="daily">Täglich</option>
                      <option value="weekly">Wöchentlich</option>
                    </select>
                  </div>
                  <div>
                    <Label>Backup-E-Mail (optional)</Label>
                    <Input
                      type="email"
                      value={settings.backup_email}
                      onChange={(e) => setSettings({...settings, backup_email: e.target.value})}
                      placeholder="Falls leer: SMTP-Adresse"
                      data-testid="backup-email"
                    />
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Leer lassen = wird an SMTP-Adresse gesendet</p>
                  </div>
                </div>
              )}

              {/* Manual Trigger */}
              <div className="pt-4 border-t border-[hsl(var(--border))]">
                <Label className="text-sm font-medium mb-2 block">Backup jetzt erstellen</Label>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">
                  Erstellt sofort ein vollständiges JSON-Backup aller Datenbank-Collections und sendet es per E-Mail.
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleTriggerBackup}
                  disabled={triggeringBackup || !settings.smtp_email}
                  data-testid="backup-trigger-button"
                >
                  {triggeringBackup ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Jetzt sichern & senden
                </Button>
                {backupResult && (
                  <Alert className={`mt-3 ${
                    backupResult.type === 'success'
                      ? 'bg-[hsl(var(--chart-2)/0.1)] border-[hsl(var(--chart-2))]'
                      : 'bg-[hsl(var(--destructive)/0.1)] border-[hsl(var(--destructive))]'
                  }`} data-testid="backup-result-alert">
                    <AlertDescription className={`text-sm ${
                      backupResult.type === 'success'
                        ? 'text-[hsl(var(--chart-2))]'
                        : 'text-[hsl(var(--destructive))]'
                    }`}>
                      {backupResult.message}
                    </AlertDescription>
                  </Alert>
                )}
                {!settings.smtp_email && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-amber-600">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>SMTP muss konfiguriert sein, damit Backups versendet werden.</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="card-shadow" data-testid="settings-health-monitor-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                System-Monitor &amp; Ausfall-Benachrichtigungen
              </CardTitle>
              <CardDescription>
                Prüft alle konfigurierten Dienste (OpenAI, Gemini, Anthropic, Azure, Stripe, SMTP, ERP, MongoDB, Object Storage, Cloud-Backup) im Intervall.
                Bei <strong>{settings.health_monitor_failure_threshold || 3} Fehlversuchen in Folge</strong> wird sofort eine Alarm-Mail versandt – und bei Wiederanlauf eine Entwarnungs-Mail.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <input type="checkbox" id="health_monitor_enabled" name="health_monitor_enabled"
                  checked={!!settings.health_monitor_enabled} onChange={handleInputChange}
                  className="h-4 w-4" data-testid="settings-health-enabled" />
                <Label htmlFor="health_monitor_enabled" className="cursor-pointer">System-Monitor aktiv</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="health_monitor_interval_minutes">Intervall (Minuten)</Label>
                  <Input id="health_monitor_interval_minutes" name="health_monitor_interval_minutes" type="number"
                    value={settings.health_monitor_interval_minutes ?? 10} onChange={handleInputChange}
                    min={1} max={120}
                    data-testid="settings-health-interval" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="health_monitor_failure_threshold">Fehlversuche vor Alarm</Label>
                  <Input id="health_monitor_failure_threshold" name="health_monitor_failure_threshold" type="number"
                    value={settings.health_monitor_failure_threshold ?? 3} onChange={handleInputChange}
                    min={1} max={10}
                    data-testid="settings-health-threshold" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="health_monitor_recipient">Empfänger (leer = Firmen-Mail)</Label>
                  <Input id="health_monitor_recipient" name="health_monitor_recipient" type="email"
                    value={settings.health_monitor_recipient || ''} onChange={handleInputChange}
                    placeholder={settings.company_email || 'admin@beispiel.ch'}
                    data-testid="settings-health-recipient" />
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    Reihenfolge: 1. Dieses Feld, 2. Firmen-Mail, 3. SMTP-Absender.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button type="button" variant="outline"
                  onClick={runHealthCheck}
                  disabled={runningHealthCheck}
                  data-testid="settings-health-run-btn">
                  {runningHealthCheck ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Jetzt alle Dienste prüfen
                </Button>
                <Button type="button" variant="ghost"
                  onClick={loadHealthStatus}
                  data-testid="settings-health-refresh-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Status aktualisieren
                </Button>
              </div>

              {healthStatus?.services?.length ? (
                <div className="rounded-md border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]">
                  {healthStatus.services.map(s => {
                    const isDown = s.state === 'down';
                    const isMisc = (s.last_message || '').startsWith('nicht konfiguriert');
                    return (
                      <div key={s.service} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`settings-health-row-${s.service}`}>
                        <span className={`inline-flex h-2.5 w-2.5 rounded-full ${isDown ? 'bg-red-500' : isMisc ? 'bg-slate-300' : 'bg-emerald-500'}`}></span>
                        <span className="font-medium flex-1">{s.label}</span>
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">{s.last_message || '-'}</span>
                        {isDown && <span className="text-xs text-red-600">· {s.consecutive_failures} Fehler</span>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  Noch keine Checks gelaufen – klicke „Jetzt alle Dienste prüfen".
                </div>
              )}

              {healthStatus?.incidents?.length ? (
                <div>
                  <div className="text-xs font-medium mb-2">Letzte Vorfälle</div>
                  <div className="rounded-md border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))] max-h-48 overflow-auto">
                    {healthStatus.incidents.slice(0, 8).map(i => (
                      <div key={i.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                        <span className={`inline-flex h-1.5 w-1.5 rounded-full ${i.event === 'down' ? 'bg-red-500' : 'bg-emerald-500'}`}></span>
                        <span className="font-medium w-40 truncate">{i.label}</span>
                        <span className="text-[hsl(var(--muted-foreground))] flex-1 truncate">{i.event === 'down' ? 'Ausfall' : 'Wiederhergestellt'} · {i.detail}</span>
                        <span className="text-[hsl(var(--muted-foreground))]">{(i.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
    </>
  );
};

export default SettingsEmail;
